<script setup lang="ts">
/**
 * NoteGripDialog
 *
 * Zentrierter modaler Dialog, der den {@link NoteGripInspector} der aktuell ausgewählten
 * Griffschrift-**Ausgabe**-Note zeigt (Instrument + gerankte Alternativen) und die gewählte
 * Alternative als `applyGrip` nach oben liefert; danach schließt der Dialog. Geöffnet über den
 * Griff-Button der `ScoreEditToolbar` **oder** aus dem `NoteActionsOverlay` (Rechtsklick-Kontextmenü,
 * der diesen Dialog verwendet).
 *
 * Muster analog `KeyboardShortcutsDialog.vue`: Backdrop-Klick schließt, ESC läuft in der
 * CAPTURE-Phase mit `stopImmediatePropagation`, damit das Schließen NICHT gleichzeitig die
 * Notenauswahl der `ScoreEditToolbar` (bubble-phase Escape) aufhebt.
 */
import {ref, watch} from 'vue';
import {Icon} from '@iconify/vue';

import type {NoteInfo} from '@/composables/musicXmlDom';
import {useCaptureEscape} from '@/composables/useCaptureEscape';
import {focusWithoutScroll} from '@/shared/focus';
import NoteGripInspector from './NoteGripInspector.vue';
import type {Grip} from './gripTypes';


// musicXml ist ein reiner INPUT (wird hier und im Inspector nie geschrieben; Griff-Änderungen
// reisen als `applyGrip`-Emit) – deshalb Prop, kein defineModel (der Parent bindet one-way).
const {selectedNote = null, musicXml = undefined} = defineProps<{
  selectedNote?: NoteInfo | null;
  musicXml?: string | null;
}>();

const emit = defineEmits<{
  applyGrip: [grip: Grip];
}>();

const isOpen = defineModel<boolean>('isOpen', {default: false, type: Boolean});

const dialogRef = ref<HTMLElement | null>(null);

function close(): void {
  isOpen.value = false;
}

function onApply(grip: Grip): void {
  emit('applyGrip', grip);
  close();
}

// ESC (Capture-Phase, nur solange offen) schließt den Dialog, ohne die bubble-phase Escape der
// ScoreEditToolbar (Auswahl aufheben) parallel feuern zu lassen.
useCaptureEscape(isOpen, close);

// Fokus in den Dialog setzen, sobald er geöffnet wird (A11y).
watch(isOpen, async (open) => {
  if (!open) return;
  await Promise.resolve();
  focusWithoutScroll(dialogRef.value);
});
</script>

<template>
  <Transition
      enter-active-class="animate-fade animate-duration-200"
      leave-active-class="transition-opacity duration-150"
      leave-to-class="opacity-0"
  >
    <div
        v-if="isOpen"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
        @click.self="close"
    >
      <div
          ref="dialogRef"
          role="dialog"
          aria-modal="true"
          aria-label="Griff der Note ändern"
          tabindex="-1"
          class="flex max-h-[90dvh] w-fit min-w-0 max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-xl bg-white shadow-xl outline-none"
      >
        <header class="flex items-center justify-between gap-3 border-b border-gray-200 px-4 py-2">
          <h2 class="flex min-w-0 items-center gap-2 text-sm font-semibold text-gray-900">
            <Icon icon="fa6-solid:hand-point-up" class="shrink-0 text-blue-500"/>
            <span class="truncate">Griff ändern</span>
          </h2>
          <button type="button" class="btn btn-ghost !px-2 !py-1.5" aria-label="Schließen" @click="close">
            <Icon icon="fa6-solid:xmark"/>
          </button>
        </header>
        <div class="min-h-0 flex-1 overflow-y-auto p-3">
          <NoteGripInspector
              v-if="musicXml"
              :music-xml="musicXml"
              :selected-note="selectedNote"
              @apply-grip="onApply"
          />
        </div>
      </div>
    </div>
  </Transition>
</template>
